import type { RouteEventData, StepEventData } from '@rango-dev/widget-embedded';
import type { FunctionComponent } from 'react';

import { WalletTypes } from '@rango-dev/provider-all';
import { useToast } from '@rango-dev/whale/lab';
import {
  MainEvents,
  RouteEventType,
  StepEventType,
  useWidgetEvents,
} from '@rango-dev/widget-embedded';
import { useEffect } from 'react';

import { numberToString } from '../../utils/Numbers';

const TOAST_HIDE_DURATION = 10_000;
const TOAST_AMOUNT_DECIMALS = 4;

export const SwapToast: FunctionComponent = () => {
  const { toast } = useToast();
  const widgetEvents = useWidgetEvents();
  const handleAddToast = (args: {
    title: string;
    type: 'success' | 'error' | 'info';
    id: string;
  }) => {
    toast({
      id: args.id,
      title: args.title,
      type: args.type,
      duration: TOAST_HIDE_DURATION,
    });
  };

  const handleRouteEvents = (widgetEvent: RouteEventData) => {
    const { event, route } = widgetEvent;
    const isFailed = event.type === RouteEventType.FAILED;
    const isSucceeded = event.type === RouteEventType.SUCCEEDED;
    const firstStep = route.steps[0];
    const lastStep = route.steps[route.steps.length - 1];

    const amountToString = (amount: string | null) =>
      numberToString(amount, TOAST_AMOUNT_DECIMALS, TOAST_AMOUNT_DECIMALS);

    const errorMessage = `Transaction failed. Failed to Swap ${amountToString(route.inputAmount)} ${
      firstStep.fromSymbol
    } to ${amountToString(lastStep.outputAmount || lastStep.expectedOutputAmountHumanReadable)} ${
      lastStep.toSymbol
    }`;
    const successMessage = `Transaction Successful! Received ${amountToString(
      lastStep.outputAmount || lastStep.expectedOutputAmountHumanReadable,
    )} ${lastStep.toSymbol} on ${lastStep.toBlockchain}`;

    if (isFailed || isSucceeded) {
      handleAddToast({
        title: isFailed ? errorMessage : successMessage,
        type: isFailed ? 'error' : 'success',
        id: route.requestId,
      });
    }
  };

  const handleStepEvents = (widgetEvent: StepEventData) => {
    const { event, route } = widgetEvent;
    const isWalletConnect = Object.values(route.wallets).some(
      (w) => w.walletType === WalletTypes.WALLET_CONNECT_2,
    );
    if (isWalletConnect && event.type === StepEventType.TX_EXECUTION) {
      handleAddToast({
        title: 'Please confirm the transaction request in your app',
        type: 'info',
        id: route.requestId,
      });
    }
  };

  useEffect(() => {
    widgetEvents.on(MainEvents.RouteEvent, handleRouteEvents);

    return () => {
      widgetEvents.off(MainEvents.RouteEvent, handleRouteEvents);
    };
  }, [widgetEvents]);

  useEffect(() => {
    widgetEvents.on(MainEvents.StepEvent, handleStepEvents);
    return () => {
      widgetEvents.off(MainEvents.StepEvent, handleStepEvents);
    };
  }, [widgetEvents]);
  return null;
};
